import { useAuth } from "@/auth/contexts/AuthProvider";
import AssetPicker from "@/core/components/v2/AssetPicker";
import CopyToClipboardButton from "@/core/components/v2/CopyToClipboardButton";
import { SearchToolbar } from "@/core/components/v2/SearchToolbar";
import { TableTemplate } from "@/core/components/v2/TableTemplate";
import { useSnackbar } from "@/core/contexts/SnackbarProvider";
import { VideoAsset } from "@/shared-types/v2";
import { Add, Remove, VideoFile } from "@mui/icons-material";
import { CircularProgress, Tooltip } from "@mui/material";
import { Box } from "@mui/system";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import VideoPreview from "./VideoPreview";
import VideoThumbnail from "./VideoThumbnail";

export type VideoAssetPickerProps =
  | {
      onSelectionChange: (selected: VideoAsset[]) => void;
      allowSelectMultiple: true;
      initialSelectionIds?: string[];
      filterFunction?: (video: VideoAsset) => boolean;
      buttonText?: string;
    }
  | {
      allowSelectMultiple?: false;
      onSelectionChange: (selected?: VideoAsset | undefined) => void;
      initialSelectionIds?: string | undefined;
      filterFunction?: (video: VideoAsset) => boolean;
      buttonText?: string;
    };

const VideoAssetPicker = ({
  onSelectionChange,
  allowSelectMultiple,
  initialSelectionIds,
  filterFunction,
  buttonText,
}: VideoAssetPickerProps) => {
  const { equestrain, videos, setVideos } = useAuth();
  const snackbar = useSnackbar();

  const [selectedAssets, setSelectedAssets] = useState<VideoAsset[]>([]);

  const toggleAsset = (asset: VideoAsset) => {
    if (selectedAssets.find((selected) => selected.id === asset.id)) {
      setSelectedAssets(
        selectedAssets.filter((selected) => selected.id !== asset.id)
      );
      snackbar.success("Selection removed");
    } else {
      if (!allowSelectMultiple) {
        setSelectedAssets([asset]);
      } else {
        setSelectedAssets([...selectedAssets, asset]);
      }
      snackbar.success("Picked!");
    }
  };

  useEffect(() => {
    if (allowSelectMultiple) {
      onSelectionChange(selectedAssets);
    } else {
      if (selectedAssets.length === 0) {
        onSelectionChange(undefined);
      } else {
        onSelectionChange(selectedAssets[0]);
      }
    }
  // Deps intentionally omitted: listing allowSelectMultiple, onSelectionChange would change when this effect re-runs; the current trigger is deliberate.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssets]);

  useEffect(() => {
    if (videos.length === 0) {
      equestrain.Client.getAllVideos()
        .then(({ error, data }) => {
          if (error) {
            snackbar.error(error.message);
            return;
          }
          if (data) {
            const d = data.data;
            setVideos(d);
            if (initialSelectionIds) {
              setSelectedAssets(
                d.filter((video) => {
                  if (allowSelectMultiple && video?.id) {
                    return initialSelectionIds.includes(video.id);
                  }
                  return initialSelectionIds === video?.id;
                })
              );
            }
          }
        })
        .catch((error) => {
          snackbar.error(error.message);
        });
    } else {
      if (initialSelectionIds) {
        setSelectedAssets(
          videos.filter((video) => {
            if (allowSelectMultiple && video?.id) {
              return initialSelectionIds.includes(video.id);
            }
            return initialSelectionIds === video?.id;
          })
        );
      }
    }
  // Deps intentionally omitted: listing allowSelectMultiple, initialSelectionIds, videos would change when this effect re-runs; the current trigger is deliberate.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // }, [initialSelectionIds, videos]);

  return (
    <AssetPicker<VideoAsset>
      pickerFullScreen={true}
      onSelect={(selected) => console.log(selected)}
      selected={selectedAssets}
      onDelete={(val) => toggleAsset(val)}
      idFieldExtractor={(video) => video.id}
      labelFieldExtractor={(video) => video?.title || ""}
      pickerContent={
        <VideoAssetPickerContent
          videos={filterFunction ? videos.filter(filterFunction) : videos}
          toggleAsset={toggleAsset}
          selectedAssets={selectedAssets}
        />
      }
      buttonContent={
        <>
          <VideoFile />
          {buttonText || "Pick video(s)"}
        </>
      }
    />
  );
};

const VideoAssetPickerContent = ({
  videos,
  selectedAssets,
  toggleAsset,
}: {
  videos: VideoAsset[];
  selectedAssets: VideoAsset[];
  toggleAsset: (asset: VideoAsset) => void;
}) => {
  const [text, setText] = useState<string>("");
  const [filteredAssets, setFilteredAssets] = useState<VideoAsset[]>([]);
  const [previewVideo, setPreviewVideo] = useState<VideoAsset | null>(null);

  useEffect(() => {
    setFilteredAssets(
      videos.filter(
        (video) =>
          video?.title?.toLowerCase().includes(text.toLowerCase()) ||
          video?.description?.toLowerCase().includes(text.toLowerCase()) ||
          video?.internalTags?.some((tag) =>
            tag.toLowerCase().includes(text.toLowerCase())
          )
      )
    );
  }, [text, videos]);

  return (
    <Box>
      {previewVideo && (
        <VideoPreview
          onClose={() => setPreviewVideo(null)}
          videoAsset={previewVideo}
        />
      )}
      <SearchToolbar
        setText={setText}
        text={text}
        placeholder="Search by tag, name, description.."
        boxProps={{
          mb: 2,
          minWidth: "500px",
        }}
        textFieldProps={{
          fullWidth: true,
        }}
      />
      <p>
        After choosing your video you can also close this window by pressing ESC
        button
      </p>
      <AssetTable
        videos={filteredAssets}
        selectedAssets={selectedAssets}
        toggleAsset={toggleAsset}
        setPreviewVideo={setPreviewVideo}
      />
    </Box>
  );
};

const AssetTable = ({
  videos,
  toggleAsset,
  selectedAssets,
  setPreviewVideo,
}: {
  videos: VideoAsset[];
  toggleAsset: (asset: VideoAsset) => void;
  selectedAssets: VideoAsset[];
  setPreviewVideo: Dispatch<SetStateAction<VideoAsset | null>>;
}) => {
  const snackbar = useSnackbar();

  const [usePagination, setUsePagination] = useState(true);

  const [loading, setLoading] = useState("");

  const pick = async (video: VideoAsset) => {
    setLoading(video.id);
    setTimeout(() => {
      toggleAsset(video);
      setLoading("");
    }, 1000);
  };

  return (
    <TableTemplate
      useDefaultActions={false}
      data={videos}
      usePagination={usePagination}
      setUsePagination={setUsePagination}
      paginationOptions={{
        rowsPerPageOptions: [30, 50, 100, 150],
      }}
      useSort={true}
      headerFields={[
        "ID",
        "Preview",
        "Title",
        "Description",
        "Created at",
        "Internal tags",
        "Add",
      ]}
      dataFields={[
        {
          value: "id",
          render: (value) => (
            <CopyToClipboardButton
              value={value}
              // toolTip="Copy video ID"
              onValueCopied={() => snackbar.success("id copied to clipboard")}
            />
          ),
        },
        {
          value: "thumbnailUrl",
          render: (value, row) => (
            <VideoThumbnail url={value} onClick={() => setPreviewVideo(row)} />
          ),
          sortOptions: {
            disabled: true,
          },
        },
        "title",
        {
          value: "description",
          render: (value) => (
            <Tooltip title={value} placement="top">
              <span>
                {value.length > 50 ? value.slice(0, 30) + "..." : value}
              </span>
            </Tooltip>
          ),
        },
        {
          value: "muxAsset",
          formatter: (_, row) =>
            new Date(Number(row.muxAsset.created_at) * 1000).toLocaleString(),
          sortOptions: {
            sortFieldName: "Created at",
            sortValueExtractor: (row) => Number(row.muxAsset.created_at),
          },
        },
        {
          value: "internalTags",
          render: (tags) => tags?.join(", ") || "",
          sortOptions: {
            disabled: true,
          },
        },
        {
          value: "id",
          render: (id: string) => {
            const a = videos.find((video) => video.id === id);
            if (a) {
              if (selectedAssets.find((selected) => selected.id === a.id)) {
                return (
                  <AddOrRemoveButtonContainer onClick={() => pick(a)}>
                    {loading === a.id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Remove />
                    )}
                  </AddOrRemoveButtonContainer>
                );
              }
              return (
                <AddOrRemoveButtonContainer onClick={() => pick(a)}>
                  {loading === a.id ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Tooltip title="Pick this video">
                      <Add />
                    </Tooltip>
                  )}
                  {/* <Add onClick={() => toggleAsset(a)} /> */}
                </AddOrRemoveButtonContainer>
              );
            } else {
              return null;
            }
          },
          sortOptions: {
            disabled: true,
          },
        },
      ]}
      tableKeyPrefix="videotable"
      keyFieldValueExtractor={(video) => video.id}
      setData={() => {}}
      onError={() => {}}
    />
  );
};

export default VideoAssetPicker;

const AddOrRemoveButtonContainer = ({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick?: () => void;
}) => {
  return (
    <Box
      onClick={(e) => {
        e.preventDefault();
        onClick?.();
      }}
      sx={{
        cursor: "pointer",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "0.5rem",
        border: "1px solid rgba(0, 0, 0, 0.25)",
        borderRadius: "0.5rem",
      }}
    >
      {children}
    </Box>
  );
};
